import { useEffect, useRef } from 'react'
import { useAnalytics, useCart, useUser } from '@chordcommerce/react-autonomy'
import { Box } from 'theme-ui'
import * as Sentry from '@sentry/nextjs'
import CheckoutCheckoutForm from './Checkout'
import { useCheckout } from '~/context/checkout'
import { useVariantOptions } from '~/hooks/use-variant-options'
import { useRouter } from 'next/router'
import CustomCard from '~/components/CustomCard'

import { SectionHeadingGroup } from '@propertylens/ui-kit'
import { Progress } from '@nextui-org/react'
import OuterGrayContainer from '~/components/OuterGrayContainer'
import {
  CHECKOUT_STEP_INTERSTITIAL,
  CHECKOUT_STEP_CHECKOUT_COMPLETE,
  CHECKOUT_PATHS,
  CHECKOUT_STEP_DEFAULT,
} from '~/utils/constants'
import { getParam } from '~/utils/url'

const CheckoutManagerV1_3 = ({
  children,
  variants,
  showHeaderCart,
  bundleText,
  showProductDescription,
  customProductText,
  klaviyoSampleFormCtaText,
  klaviyoSampleFormId,
  showPromoCode = true,
  customRadioLabel,
  redirectParameters,
}) => {
  const { chordAnalytics } = useAnalytics()
  const { forgetCart } = useCart()
  const { step } = useCheckout()
  const previousStep = useRef<string | null>()
  const router = useRouter()
  const { user } = useUser()
  const { variantOptions } = useVariantOptions({
    builderVariants: variants?.map((variant) => ({
      ...variant?.sku?.value?.data,
    })),
  })
  const userEmail = user?.data?.email
  const userId = user?.data?.id?.toString()

  useEffect(()=>{
    forgetCart();
  }, []);

  useEffect(()=>{
    try {
      const replay = Sentry.getReplay()
      replay.start()
      return () => {
        replay?.stop()
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }, [])

  // When the current step changes:
  // - update the URL, which also triggers a page event, and
  // - scroll to the top of the step (especially important on mobile)
  // The Chord SDK triggers a page event on initial load and subsequent URL
  // changes. This prevents duplicate page views on load.
  useEffect(() => {
    if (router.isReady) {
      if (!previousStep.current && !CHECKOUT_PATHS[step]) {
        previousStep.current = step
      }
      if (previousStep.current === step) return
      const urlStep = getParam('step')

      if (urlStep !== CHECKOUT_PATHS[step]) {
        const query = { ...router.query }
        if (CHECKOUT_PATHS[step]) {
          query.step = CHECKOUT_PATHS[step]
        } else if (query.step) {
          query.step = undefined
        }

        router.replace({ query }, undefined, {
          shallow: true,
        })
      }

      previousStep.current = step

      window.scrollTo(0, 0)
    }
  }, [step, router.isReady])

  useEffect(() => {
    if (userEmail && userId) {
      chordAnalytics.trackSignedIn({ email: userEmail, userId })
    }
  }, [chordAnalytics, userEmail, userId])

  /*
    Direct to checkout step, always the first step
  */
  if (step === CHECKOUT_STEP_DEFAULT || step === CHECKOUT_STEP_INTERSTITIAL) {
    return (
      <Box className="">
        <CheckoutCheckoutForm
          variantOptions={variantOptions}
          showHeaderCart={showHeaderCart}
          showPromoCode={showPromoCode}
          bundleText={bundleText || 'Multiple addresses? Bundle & SAVE!'}
          showProductDescription={showProductDescription}
          customProductText={customProductText}
          klaviyoSampleFormCtaText={klaviyoSampleFormCtaText}
          klaviyoSampleFormId={klaviyoSampleFormId}
          customRadioLabel={
            customRadioLabel ||
            'Choose number of report credits to buy... credits never expire!'
          }
          redirectParameters={redirectParameters}
        />
      </Box>
    )
  }

  /*
    This step shows once the completes there purchase. User will eventually get redirected
  */
  if (step === CHECKOUT_STEP_CHECKOUT_COMPLETE) {
    return (
      <Box className="gtm-order-completed">
        <CustomCard>
          <OuterGrayContainer className="buyer-container-head mb-5">
            <SectionHeadingGroup
              heading="Thank you for your order! We're now redirecting you to create an account and view your PropertyLens Report..."
              iconDesktop="lens-buyer-lg"
              level={5}
              className="mb-8 lg:gap-0"
            />
            <Progress
              isIndeterminate
              size="sm"
              aria-label="Loading..."
              className="mb-6"
            />
          </OuterGrayContainer>
        </CustomCard>
      </Box>
    )
  }

  if (children) {
    return <Box>{children}</Box>
  }

  return null
}

export default CheckoutManagerV1_3
